import React, { useEffect, useState, useMemo } from "react"
import { gql, useQuery } from "@apollo/client"
import PPLoadingIndicator from "../layout/PPLoadingIndicator"
import PPErrorDisplay from "../layout/PPErrorDisplay"
import PPHotProductItem from "./PPHotProductItem"
import { useTranslation } from "react-i18next"
import { PPDefaultSelector } from "../ui/PPDefaultSelector"
import { MultiValueLabel, Option } from "../pricetable/PPShopPreset"
import { components } from "react-select"
import "../../styles/dashboard.css"

const EMPTY_SHOPS = []

const QUERY = gql`
    query getStats($department: String, $shopIds: [Long]) {
        stats(department: $department, shopIds: $shopIds) {
            topProducts {
                customerId
                id
                lastPriceChangesOverAllShops {
                    price
                    shopId
                    shopName
                    lang
                    crawlDate
                }
                priceChangesLast5Days
                priceTrendToCustomerPrice
                productImage
                productName
                productnr
                productProducer
                price
                minPrice
            }
        }
    }
`

const QUERY_OWNTOPPRODUCTS = gql`
    query getStatsOwnTopProducts($ownProductsnrs: String, $shopIds: [Long]) {
        statsOwnTopProducts(ownProductsnrs: $ownProductsnrs, shopIds: $shopIds) {
            customerId
            id
            lastPriceChangesOverAllShops {
                price
                shopId
                shopName
                lang
                crawlDate
            }
            priceChangesLast5Days
            priceTrendToCustomerPrice
            productImage
            productName
            productnr
            productProducer
            price
            minPrice
        }
    }
`

function PPTopProducts(props) {
    const [t, i18n] = useTranslation()
    const [selectedShopId, setSelectedShopId] = useState(null)
    const [selectedLang, setSelectedLang] = useState(null)
    const [customerActiveShops, setCustomerActiveShops] = useState([])
    const [showMore, setShowMore] = useState(false)

    useEffect(() => {
        const next =
            props.customerActiveShops && props.customerActiveShops.length > 0
                ? props.customerActiveShops
                : EMPTY_SHOPS
        setCustomerActiveShops((prev) => (prev === next || (prev.length === 0 && next.length === 0) ? prev : next))
    }, [props.customerActiveShops])

    const languages = useMemo(() => {
        if (!customerActiveShops || customerActiveShops.length === 0) return []

        const uniqueLangs = [...new Set(customerActiveShops.map((shop) => shop.lang).filter(Boolean))]
        return uniqueLangs.map((lang) => ({
            value: lang,
            label: lang.toUpperCase(),
            lang: lang
        }))
    }, [customerActiveShops])

    const shopsForSelectedLang = useMemo(() => {
        if (!customerActiveShops || customerActiveShops.length === 0) return []

        if (!selectedLang) return []

        const filteredShops = customerActiveShops.filter((shop) => shop.lang === selectedLang)

        return filteredShops.map((shop) => ({
            value: shop.id,
            label: shop.name,
            lang: shop.lang,
            name: shop.name
        }))
    }, [customerActiveShops, selectedLang])

    useEffect(() => {
        if (selectedLang === null && languages.length > 0) {
            setSelectedLang(languages[0]?.value)
        }
    }, [languages, selectedLang])

    useEffect(() => {
        if (selectedLang === null) {
            return
        }
        const shopsForLang = customerActiveShops.filter((shop) => shop.lang === selectedLang)
        if (shopsForLang.length === 0) {
            setSelectedShopId(null)
            return
        }
        const currentShop = customerActiveShops.find(
            (shop) => shop.id === selectedShopId && shop.lang === selectedLang
        )
        if (!currentShop) {
            setSelectedShopId(shopsForLang[0].id)
        }
    }, [selectedLang, customerActiveShops, selectedShopId])

    const hasShops = customerActiveShops && customerActiveShops.length > 0
    const topProductsOwnProductsnrs = localStorage.getItem("dashboard_topproducts_own_nrs")
    const isOwnTopProducts =
        topProductsOwnProductsnrs != null &&
        topProductsOwnProductsnrs != "" &&
        topProductsOwnProductsnrs != "0"
    const waitForShop = isOwnTopProducts && hasShops && selectedShopId == null

    const variables = useMemo(() => {
        const vars = {
            department:
                props?.changeFilterDepartment != null && props?.changeFilterDepartment?.name != ""
                    ? props?.changeFilterDepartment?.name
                    : ""
        }
        if (isOwnTopProducts) {
            vars.ownProductsnrs = topProductsOwnProductsnrs
            if (selectedShopId !== null) {
                vars.shopIds = [selectedShopId]
            }
        }
        return vars
    }, [props?.changeFilterDepartment, selectedShopId, isOwnTopProducts, topProductsOwnProductsnrs])

    const query = isOwnTopProducts ? QUERY_OWNTOPPRODUCTS : QUERY

    const { loading, error, data } = useQuery(query, {
        displayName: "PPTopProductsQuery",
        variables,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
        skip: waitForShop
    })

    if (error)
        return (
            <div className="col-lg-12">
                <PPErrorDisplay error={error} />
            </div>
        )
    if (waitForShop || (loading && !data)) return <PPLoadingIndicator />

    const selectedLangOption = languages.find((opt) => opt.value === selectedLang) || languages[0]

    const shopOptions = shopsForSelectedLang
    const selectedShopOption =
        shopOptions.find((opt) => opt.value === selectedShopId) || shopOptions[0] || null

    const SingleValueWithFlag = ({ children, ...props }) => {
        const { data } = props
        return (
            <components.SingleValue {...props}>
                {data.lang && <span className={"fi fi-" + data.lang} style={{ marginRight: "6px" }} />}
                {children}
            </components.SingleValue>
        )
    }

    const SingleValueShopWithFlag = ({ children, ...props }) => {
        const { data } = props
        return (
            <components.SingleValue {...props}>
                {data.lang && <span className={"fi fi-" + data.lang} style={{ marginRight: "6px" }} />}
                {children}
            </components.SingleValue>
        )
    }

    return (
        <div className="col-lg-12 connectedSortable ui-sortable" id="divTopProducts">
            <div className="card shadow d-flex flex-fill">
                <div className="card-header d-flex flex-wrap align-items-center gap-2">
                    <h3 className="card-title mb-0">
                        {t("dashboard.top_products")} {data?.statsOwnTopProducts && <>*</>}
                    </h3>
                    {hasShops && (
                        <div className="pp-card-header-tools">
                            <div style={{ minWidth: "100px" }}>
                                <PPDefaultSelector
                                    id="selectTopProductsLang"
                                    data={languages}
                                    components={{
                                        MultiValueLabel,
                                        Option,
                                        SingleValue: SingleValueWithFlag
                                    }}
                                    placeholder={t("single.language")}
                                    onChange={(option) => {
                                        setSelectedShopId(null)
                                        setSelectedLang(option?.value || null)
                                    }}
                                    value={selectedLangOption}
                                    isClearable={false}
                                />
                            </div>
                            <div style={{ minWidth: "200px" }}>
                                <PPDefaultSelector
                                    id="selectTopProductsShop"
                                    data={shopOptions}
                                    components={{
                                        MultiValueLabel,
                                        Option,
                                        SingleValue: SingleValueShopWithFlag
                                    }}
                                    placeholder={t("single.shops")}
                                    onChange={(option) => {
                                        if (!option) {
                                            if (shopsForSelectedLang.length > 0) {
                                                setSelectedShopId(shopsForSelectedLang[0].value)
                                            } else {
                                                setSelectedShopId(null)
                                            }
                                        } else {
                                            setSelectedShopId(option.value)
                                        }
                                    }}
                                    value={selectedShopOption}
                                    isDisabled={!selectedLang || shopsForSelectedLang.length === 0}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="card-body">
                    {(isOwnTopProducts ? data?.statsOwnTopProducts : data?.stats?.topProducts)
                        ?.slice(
                            0,
                            showMore
                                ? (isOwnTopProducts ? data?.statsOwnTopProducts : data?.stats?.topProducts)?.length
                                : 3
                        )
                        ?.map((product) => (
                            <PPHotProductItem
                                key={product.id}
                                product={product}
                                t={t}
                                i18n={i18n}
                                tariffComparisonMode={props?.tariffComparisonMode}
                                selectedShopId={isOwnTopProducts ? selectedShopId : null}
                                selectedShop={isOwnTopProducts ? selectedShopOption : null}
                            />
                        ))}
                </div>
                <div className="card-footer text-center">
                    <button
                        type="button"
                        style={{
                            cursor: "pointer",
                            borderRadius: "0px 0px 3px 3px",
                            color: "grey",
                            background: "none",
                            border: "none",
                            padding: 0
                        }}
                        className={"small-box-footer"}
                        onClick={() => setShowMore(!showMore)}>
                        {showMore ? t("single.less") : t("single.more")}{" "}
                        <i className={showMore ? "fas fa-arrow-circle-up" : "fas fa-arrow-circle-right"} />
                    </button>
                </div>
            </div>
        </div>
    )
}

export default PPTopProducts
